import { useState } from "react";
import { toast } from "sonner";
import { Send, ArrowRight, CheckCircle2, Stethoscope, ShieldCheck, GraduationCap, Award } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useCourseOptions } from "@/lib/useCourseOptions";

export default function Consultancy({ data, onEnquire }) {
  const handleCardEnquire = (program) => {
    const label = program.code
      ? `${program.code} — ${program.title || program.code}`
      : (program.title || "Consultancy");
    if (typeof onEnquire === "function") onEnquire(label);
  };

  return (
    <section id="consultancy" data-testid="section-consultancy" className="section-pad bg-white relative overflow-hidden">
      {/* subtle background accents */}
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full bg-teal-50 -z-0" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#1E3A8A]/[0.05] -z-0" />

      <div className="container-pad relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* LEFT: heading + programs + highlights */}
        <div className="lg:col-span-7">
          <div className="kicker">{data.kicker || "Sunbright Medical Consultancy"}</div>
          <h2 className="heading-main mt-4">{data.title}</h2>
          <p className="heading-sub max-w-2xl">{data.description}</p>

          <div className="mt-9 grid sm:grid-cols-2 gap-5">
            {(data.programs || []).map((p, i) => (
              <article
                key={i}
                className="group relative flex flex-col bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_1px_2px_rgba(15,23,42,0.03)] hover:shadow-[0_14px_36px_-14px_rgba(30,58,138,0.28)] hover:-translate-y-0.5 transition"
                data-testid={`consultancy-program-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-14 h-14 rounded-xl bg-[#1E3A8A] text-white grid place-items-center font-extrabold text-sm tracking-wide shadow-sm group-hover:bg-teal-600 transition">
                    {p.code || "•"}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-teal-700">{p.code}</div>
                    <div className="mt-1 text-sm font-semibold text-[#0F172A] leading-snug">{p.title}</div>
                  </div>
                </div>

                {/* Spacer pushes CTA to the bottom for equal padding across cards */}
                <div className="mt-6 pt-5 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => handleCardEnquire(p)}
                    data-testid={`consultancy-program-cta-${i}`}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0D9488] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#0F766E] hover:shadow-md transition group-hover:translate-y-0"
                  >
                    Enquire Now <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>

          {(data.highlights || []).length > 0 && (
            <ul className="mt-9 grid sm:grid-cols-2 gap-3" data-testid="consultancy-highlights">
              {data.highlights.map((h, i) => {
                const Icon = [Stethoscope, GraduationCap, ShieldCheck, Award][i % 4];
                return (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                    <span className="w-7 h-7 rounded-full bg-teal-50 text-teal-700 grid place-items-center shrink-0">
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <span>{h}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* RIGHT: lead form */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <ConsultancyForm data={data} />
        </div>
      </div>
    </section>
  );
}

function ConsultancyForm({ data }) {
  const options = useCourseOptions();
  const preferred = (data.programs || []).slice(0, 4).map((p) => (p.code ? `${p.code} — ${p.title || p.code}` : p.title));
  const [form, setForm] = useState({ name: "", mobile: "", course: preferred[0] || options[0] || "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Please enter your name and mobile number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "consultancy" });
      setDone(true);
      toast.success("Request received. Our consultant will call you soon.");
      setForm({ name: "", mobile: "", course: preferred[0] || options[0] || "", message: "" });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-[0_12px_40px_-14px_rgba(15,23,42,0.22)] bg-white" data-testid="consultancy-form-card">
      <div className="px-7 md:px-8 py-7 bg-gradient-to-br from-[#0B1736] via-[#0F1E43] to-[#1E3A8A] text-white">
        <div className="text-xs tracking-[0.18em] uppercase font-semibold text-teal-300">Free · No obligation</div>
        <h3 className="mt-2 text-2xl font-bold leading-snug">{data.formTitle || "Talk to a Consultant"}</h3>
        <p className="mt-2 text-sm text-slate-200/85">{data.formSubtitle || "Share your details and our medical admissions counsellor will call you back."}</p>
      </div>

      {done ? (
        <div className="p-8 text-center" data-testid="consultancy-form-success">
          <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto text-teal-700">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h4 className="mt-4 font-bold text-lg text-[#0F172A]">Request received</h4>
          <p className="text-sm text-slate-600 mt-1">Our consultant will call you within one business day.</p>
          <button onClick={() => setDone(false)} className="btn-ghost mt-6" data-testid="consultancy-form-new">Submit another</button>
        </div>
      ) : (
        <form onSubmit={submit} className="p-7 md:p-8 grid gap-4" data-testid="consultancy-form">
          <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Your full name" testid="consultancy-name-input" required />
          <Field label="Mobile Number" type="tel" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v.replace(/[^\d+ -]/g, "") })} placeholder="+91 9XXXXXXXXX" testid="consultancy-mobile-input" required />
          <SelectField
            label="Interested Program"
            value={form.course}
            onChange={(v) => setForm({ ...form, course: v })}
            options={options}
            testid="consultancy-course-select"
          />
          <TextAreaField label="Your Message / Query" value={form.message} onChange={(v) => setForm({ ...form, message: v })} placeholder="Tell us about your NEET score or any specific questions..." testid="consultancy-message-input" />
          <button type="submit" disabled={submitting} data-testid="consultancy-submit-btn" className="btn-accent w-full disabled:opacity-60">
            {submitting ? "Submitting..." : <>{data.ctaLabel || "Request Consultation"} <Send className="w-4 h-4" /></>}
            {!submitting && <ArrowRight className="w-4 h-4" />}
          </button>
          <p className="text-[11px] text-slate-400 text-center">By submitting, you agree to be contacted by Sunbright regarding your consultation.</p>
        </form>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
      />
    </div>
  );
}
function TextAreaField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15 resize-none"
      />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:border-[#1E3A8A] focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/15"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
